import React, { useEffect, useState } from 'react';
import { Table, Button, Modal, Form, Input, InputNumber, Select, message, Upload, Image, Card, Row, Col } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import { getProducts, createProduct, updateProduct, deleteProduct, createBulkProducts, getFilterOptions, updatePricesBulk } from '../../../redux/actions/product';
import { getCategories } from '../../../redux/actions/category';
import { CloudUploadOutlined, DeleteOutlined, UploadOutlined } from '@ant-design/icons';
import ProtectedLayout from '../../../layouts/ProtectedLayout';
import type { UploadFile } from 'antd/es/upload/interface';
import BulkExcelPasteTable from '../Shared/BulkInsertFromExcel';
import { columnMappings } from '../Shared/utility';
import ExportExcel from '../Shared/ExportExcel';
import { v4 as uuidv4 } from 'uuid';

const Products = () => {
    const [form] = Form.useForm();
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [editingProduct, setEditingProduct] = useState<any>(null);
    const [isBulkModalVisible, setIsBulkModalVisible] = useState(false);
    const [fileList, setFileList] = useState<UploadFile[]>([]);

    // Toplu fiyat güncelleme states
    const [priceUpdatePercentage, setPriceUpdatePercentage] = useState<number>(0);
    const [isPriceUpdateLoading, setIsPriceUpdateLoading] = useState(false);

    // Filter states
    const [filters, setFilters] = useState({
        search: '',
        brand: '',
        tire_type: '',
        season: '',
        tire_size: '',
        production_year: '',
        category: '',
        load_index: '',
        speed_index: '',
        page: 1,
        limit: 10
    });

    const dispatch = useDispatch();
    const { products, totalPages, currentPage, totalCount, filterOptions } = useSelector((state: any) => state.products);
    const { categories } = useSelector((state: any) => state.categories);

    // URL'leri WebP formatına çevir
    const convertToWebP = (url: string) => {
        if (!url) return url;
        return url.replace(/\.(png|jpg|jpeg|gif)$/i, '.webp');
    };

    useEffect(() => {
        dispatch<any>(getProducts({}));
        dispatch<any>(getCategories());
        dispatch<any>(getFilterOptions());
    }, [dispatch]);

    const handleFilterChange = (key: string, value: any) => {
        // Eğer value bir string ise sondaki boşlukları temizle (copy-paste durumları için)
        const cleanedValue = typeof value === 'string' ? value.trim() : value;
        const newFilters = { ...filters, [key]: cleanedValue, page: 1 };
        setFilters(newFilters);

        // API için filtre parametrelerini hazırla
        const apiParams: any = {
            page: 1,
            limit: newFilters.limit
        };

        // Sadece dolu olan filtreleri ekle
        if (newFilters.search) apiParams.search = newFilters.search;
        if (newFilters.brand) apiParams.brand = [newFilters.brand];
        if (newFilters.tire_type) apiParams.tire_type = [newFilters.tire_type];
        if (newFilters.season) apiParams.season = [newFilters.season];
        if (newFilters.tire_size) apiParams.tire_size = newFilters.tire_size;
        if (newFilters.production_year) apiParams.production_year = newFilters.production_year;
        if (newFilters.category) apiParams.category = [newFilters.category];
        if (newFilters.load_index) apiParams.load_index = [newFilters.load_index];
        if (newFilters.speed_index) apiParams.speed_index = [newFilters.speed_index];

        dispatch<any>(getProducts(apiParams));
    };

    const clearFilters = () => {
        const clearedFilters = {
            search: '',
            brand: '',
            tire_type: '',
            season: '',
            tire_size: '',
            production_year: '',
            category: '',
            load_index: '',
            speed_index: '',
            page: 1,
            limit: 10
        };
        setFilters(clearedFilters);
        dispatch<any>(getProducts({}));
    };

    // Toplu fiyat güncelleme fonksiyonu
    const handleBulkPriceUpdate = async (operation: 'increase' | 'decrease') => {
        if (priceUpdatePercentage <= 0) {
            message.error('Lütfen geçerli bir yüzde değeri girin');
            return;
        }

        if (priceUpdatePercentage > 100) {
            message.error('Yüzde değeri 100\'den büyük olamaz');
            return;
        }

        setIsPriceUpdateLoading(true);

        try {
            // Mevcut filtreleri kullan
            const filterData = {
                search: filters.search, // EKLENDİ: Arama filtresini dahil et
                category: filters.category,
                brand: filters.brand,
                season: filters.season,
                tire_type: filters.tire_type,
                tire_size: filters.tire_size,
                production_year: filters.production_year,
                load_index: filters.load_index,
                speed_index: filters.speed_index
            };

            const result = await dispatch<any>(updatePricesBulk({
                percentage: priceUpdatePercentage,
                operation,
                filters: filterData
            }));

            message.success(result.message);

            // Ürünleri yeniden yükle
            dispatch<any>(getProducts(filters));

            // Input'u temizle
            setPriceUpdatePercentage(0);

        } catch (error: any) {
            message.error(error.response?.data?.message || 'Fiyat güncellenirken hata oluştu');
        } finally {
            setIsPriceUpdateLoading(false);
        }
    };

    const showModal = (product?: any) => {
        if (product) {
            setEditingProduct(product);

            // Tire modeli için özel form değerleri hazırla
            const formValues = {
                name: product.name,
                brand_name: product.brand_name,
                tire_type: product.tire_type,
                tire_size: product.tire_size,
                price: product.price,
                discounted_price: product.discounted_price,
                production_year: product.production_year,
                stock_amount: product.stock_amount,
                category: product.category?._id || product.category,
                // Properties içindeki değerler
                season: product.properties?.season,
                width: product.properties?.width,
                aspect_ratio: product.properties?.aspect_ratio,
                rim_diameter: product.properties?.rim_diameter,
                load_index: product.properties?.load_index,
                speed_index: product.properties?.speed_index,
                fuel_efficiency: product.properties?.fuel_efficiency,
                wet_grip: product.properties?.wet_grip,
                noise_decibel: product.properties?.noise_decibel,
            };

            form.setFieldsValue(formValues);

            // Mevcut resimleri fileList'e ekle - WebP formatına çevir
            if (product.downloaded_images && product.downloaded_images.length > 0) {
                const files = product.downloaded_images.map((image: string, index: number) => ({
                    uid: `-${index}`,
                    name: `image-${index}`,
                    status: 'done',
                    url: convertToWebP(`/uploads/${image}`)
                }));
                setFileList(files);
            }
        } else {
            setEditingProduct(null);
            form.resetFields();
            setFileList([]);
        }
        setIsModalVisible(true);
    };

    const handleCancel = () => {
        setIsModalVisible(false);
        form.resetFields();
        setEditingProduct(null);
        setFileList([]);
    };

    const handleSubmit = async (values: any) => {
        try {
            const formData = new FormData();

            // Resim dosyalarını ekle
            fileList.forEach((file: any) => {
                if (file.originFileObj) {
                    formData.append('images', file.originFileObj);
                }
            });

            // Tire modeli için properties objesi oluştur
            const properties = {
                season: values.season,
                width: values.width,
                aspect_ratio: values.aspect_ratio,
                rim_diameter: values.rim_diameter,
                load_index: values.load_index,
                speed_index: values.speed_index,
                fuel_efficiency: values.fuel_efficiency,
                wet_grip: values.wet_grip,
                noise_decibel: values.noise_decibel,
            };

            // Properties'i JSON string olarak ekle
            formData.append('properties', JSON.stringify(properties));

            // Ana ürün verilerini ekle
            const productFields = ['name', 'brand_name', 'tire_type', 'tire_size', 'price', 'discounted_price', 'production_year', 'stock_amount', 'category'];
            productFields.forEach(field => {
                if (values[field] !== undefined && values[field] !== null) {
                    formData.append(field, values[field]);
                }
            });

            // UUID ve slug oluştur (yeni ürün için)
            if (!editingProduct) {
                formData.append('id', uuidv4());

                // Basit slug oluşturma
                const slug = values.name.toLowerCase()
                    .replace(/ğ/g, 'g').replace(/ü/g, 'u').replace(/ş/g, 's')
                    .replace(/ı/g, 'i').replace(/ö/g, 'o').replace(/ç/g, 'c')
                    .replace(/[^a-z0-9]/g, '-').replace(/-+/g, '-').replace(/^-|-$/g, '');
                formData.append('slug', slug);
            }

            if (editingProduct) {
                await dispatch<any>(updateProduct(editingProduct._id, formData));
                message.success('Ürün başarıyla güncellendi');
            } else {
                await dispatch<any>(createProduct(formData));
                message.success('Ürün başarıyla oluşturuldu');
            }

            handleCancel();
            // Sayfayı yenile
            dispatch<any>(getProducts({ page: currentPage, limit: 10 }));
        } catch (error: any) {
            console.error('Hata:', error);
            message.error(error.response?.data?.message || 'Bir hata oluştu');
        }
    };

    const handleDelete = async (id: string) => {
        try {
            await dispatch<any>(deleteProduct(id));
            message.success('Ürün başarıyla silindi');
        } catch (error) {
            message.error('Bir hata oluştu');
        }
    };

    const columns: any = [
        {
            title: 'Resim',
            key: 'image',
            render: (text: string, record: any) => {
                let imageUrl = record.downloaded_images && record.downloaded_images.length > 1
                    ? `/uploads/${record.downloaded_images[1]}` // İkinci resim genelde ürün resmi
                    : record.downloaded_images && record.downloaded_images.length > 0
                        ? `/uploads/${record.downloaded_images[0]}` // İlk resim logo olabilir
                        : '/images/no-image.webp';

                // PNG/JPG uzantılarını WebP'ye çevir
                imageUrl = convertToWebP(imageUrl);

                return (
                    <Image
                        src={imageUrl}
                        width={100}
                        height={100}
                        className='rounded-md shadow-lg object-cover'
                        fallback='/images/no-image.webp'
                    />
                );
            }
        },
        {
            title: 'Ürün Adı',
            dataIndex: 'name',
            key: 'name',
            sorter: (a: any, b: any) => a.name.localeCompare(b.name),
            filterSearch: true,
            filters: products.map((product: any) => ({
                text: product.name,
                value: product.name,
            })),
            onFilter: (value: string, record: any) => record.name.includes(value),
        },
        {
            title: 'Marka',
            dataIndex: 'brand_name',
            key: 'brand_name',
            sorter: (a: any, b: any) => (a.brand_name || '').localeCompare(b.brand_name || ''),
            filterSearch: true,
            filters: [...new Set(products.map((product: any) => product.brand_name))].filter(Boolean).map((brand) => ({
                text: brand,
                value: brand,
            })),
            onFilter: (value: string, record: any) => record.brand_name === value,
        },
        {
            title: 'Kategori',
            dataIndex: ['category', 'name'],
            key: 'category',
            sorter: (a: any, b: any) => {
                const categoryA = categories?.find((c: any) => c._id === a.category?._id);
                const categoryB = categories?.find((c: any) => c._id === b.category?._id);
                return (categoryA?.name || '').localeCompare(categoryB?.name || '');
            },
            filters: categories?.map((category: any) => ({
                text: category.name,
                value: category._id,
            })) || [],
            onFilter: (value: string, record: any) => record.category?._id === value,
            render: (text: string, record: any) => {
                const category = categories?.find((c: any) => c._id === record.category?._id);
                return category ? category.name : 'Bilinmiyor';
            }
        },
        {
            title: 'Normal Fiyat',
            dataIndex: 'price',
            key: 'price',
            sorter: (a: any, b: any) => (a.price || 0) - (b.price || 0),
            render: (price: number) => <div className={`${price ? '' : 'text-gray-500'}`}>
                ₺{(price || 0).toLocaleString('tr-TR')}
            </div>,
        },
        {
            title: 'İndirimli Fiyat',
            dataIndex: 'discounted_price',
            key: 'discounted_price',
            sorter: (a: any, b: any) => (a.discounted_price || 0) - (b.discounted_price || 0),
            render: (price: number) => price ? (
                <div className='text-green-600 font-semibold'>
                    ₺{price.toLocaleString('tr-TR')}
                </div>
            ) : <span className='text-gray-400'>-</span>,
        },
        {
            title: 'Lastik Ölçüleri',
            render: (text: string, record: any) => (
                <div>
                    {record.tire_size ||
                        (record.properties?.width && record.properties?.aspect_ratio && record.properties?.rim_diameter
                            ? `${record.properties.width}/${record.properties.aspect_ratio}/R${record.properties.rim_diameter}`
                            : 'N/A')}
                </div>
            )
        },
        {
            title: '',
            key: 'actions',
            width: 200,
            align: 'right',
            render: (_: any, record: any) => (
                <div className="space-x-2">
                    <Button type="primary" onClick={() => showModal(record)}>
                        Düzenle
                    </Button>
                    <Button type="primary" danger shape="circle" icon={<DeleteOutlined />} onClick={() => handleDelete(record._id)} />
                </div>
            ),
        },
    ];

    return (
        <ProtectedLayout>
            <BulkExcelPasteTable
                modelFields={columns}
                isVisible={isBulkModalVisible}
                onClose={() => setIsBulkModalVisible(false)}
                onSubmit={(data) => {
                    const formData = new FormData();
                    data.forEach((item: any) => {
                        formData.append('products', JSON.stringify(item));
                    });
                    dispatch(createBulkProducts(formData));
                    setIsBulkModalVisible(false);
                }}
            />
            <div className="p-6">
                <div className="flex justify-between items-center mb-6">
                    <h1 className="text-2xl font-bold">Ürünler ({totalCount || products?.length || 0})</h1>
                    <div className="space-x-2">
                        <Button
                            type="primary"
                            onClick={() => setIsBulkModalVisible(true)}
                            icon={<CloudUploadOutlined />}
                        >
                            Toplu Veri Yükle
                        </Button>
                        <ExportExcel excelData={products} fileName={"Ürünler Listesi"} columnMappings={columnMappings(columns)} />
                        <Button type="primary" onClick={() => showModal()}>
                            Yeni Ürün Ekle
                        </Button>
                    </div>
                </div>

                {/* Filtreleme Arayüzü */}
                <Card title="Filtreleme ve Arama" className="mb-6">
                    <Row gutter={[16, 16]}>
                        <Col span={8}>
                            <Input.Search
                                placeholder="🔍 Ürün adı, marka veya ebat ara... (örn: Michelin, 185/65R15)"
                                value={filters.search}
                                onChange={(e) => handleFilterChange('search', e.target.value)}
                                onSearch={(value) => handleFilterChange('search', value)}
                                onPaste={async (e) => {
                                    // Paste eventinde de trimle - Clipboard API ile güvenli
                                    try {
                                        const clipboardData = e.clipboardData;
                                        if (clipboardData) {
                                            const pastedText = clipboardData.getData('text');
                                            if (pastedText) {
                                                const trimmedText = pastedText.trim();
                                                // Eğer trim edilmiş versi farklıysa, input'u güncelle
                                                if (trimmedText !== pastedText) {
                                                    e.preventDefault(); // Default paste'i engelle
                                                    handleFilterChange('search', trimmedText);
                                                }
                                            }
                                        }
                                    } catch (error) {
                                        console.log('Paste event error:', error);
                                        // Fallback - normal paste işlemi devam etsin
                                    }
                                }}
                                allowClear
                            />
                        </Col>
                        <Col span={4}>
                            <Select
                                placeholder="🏷️ Marka Seçin"
                                value={filters.brand}
                                onChange={(value) => handleFilterChange('brand', value)}
                                allowClear
                                className="w-full"
                                showSearch
                                filterOption={(input, option) =>
                                    String(option?.children || '').toLowerCase().indexOf(input.toLowerCase()) >= 0
                                }
                            >
                                {filterOptions?.brands?.map((brand: any) => (
                                    <Select.Option key={brand.name} value={brand.name}>
                                        {brand.name} ({brand.count})
                                    </Select.Option>
                                ))}
                            </Select>
                        </Col>
                        <Col span={4}>
                            <Select
                                placeholder="🔧 Lastik Tipi Seçin"
                                value={filters.tire_type}
                                onChange={(value) => handleFilterChange('tire_type', value)}
                                allowClear
                                className="w-full"
                            >
                                {filterOptions?.tireTypes?.filter((type: any) => type.name && type.name.trim() !== '').map((type: any) => (
                                    <Select.Option key={type.name} value={type.name}>
                                        {type.name} ({type.count})
                                    </Select.Option>
                                ))}
                            </Select>
                        </Col>
                        <Col span={4}>
                            <Select
                                placeholder="🌡️ Sezon Seçin"
                                value={filters.season}
                                onChange={(value) => handleFilterChange('season', value)}
                                allowClear
                                className="w-full"
                            >
                                {filterOptions?.seasons?.map((season: any) => (
                                    <Select.Option key={season.name} value={season.name}>
                                        {season.name} ({season.count})
                                    </Select.Option>
                                ))}
                            </Select>
                        </Col>
                        <Col span={4}>
                            <Button type="default" onClick={clearFilters} className="w-full">
                                Temizle
                            </Button>
                        </Col>
                    </Row>
                    <Row gutter={[16, 16]} className="mt-4">
                        <Col span={8}>
                            <Select
                                placeholder="📏 Lastik Ebatı Seçin (örn: 185/65/R15)"
                                value={filters.tire_size}
                                onChange={(value) => handleFilterChange('tire_size', value)}
                                allowClear
                                className="w-full"
                                showSearch
                                filterOption={(input, option) =>
                                    String(option?.children || '').toLowerCase().indexOf(input.toLowerCase()) >= 0
                                }
                            >
                                {filterOptions?.tireSizes?.map((size: string) => (
                                    <Select.Option key={size} value={size}>
                                        {size}
                                    </Select.Option>
                                ))}
                            </Select>
                        </Col>
                        <Col span={4}>
                            <Select
                                placeholder="📅 Üretim Yılı Seçin"
                                value={filters.production_year}
                                onChange={(value) => handleFilterChange('production_year', value)}
                                allowClear
                                className="w-full"
                            >
                                {['2024', '2023', '2022', '2021', '2020', '2019', '2018'].map((year: string) => (
                                    <Select.Option key={year} value={year}>
                                        {year}
                                    </Select.Option>
                                ))}
                            </Select>
                        </Col>
                        <Col span={4}>
                            <Select
                                placeholder="⚖️ Yük İndeksi Seçin"
                                value={filters.load_index}
                                onChange={(value) => handleFilterChange('load_index', value)}
                                allowClear
                                className="w-full"
                                showSearch
                                filterOption={(input, option) =>
                                    String(option?.children || '').toLowerCase().indexOf(input.toLowerCase()) >= 0
                                }
                            >
                                {filterOptions?.loadIndexes?.map((index: string) => (
                                    <Select.Option key={index} value={index}>
                                        {index}
                                    </Select.Option>
                                ))}
                            </Select>
                        </Col>
                        <Col span={4}>
                            <Select
                                placeholder="🚀 Hız İndeksi Seçin"
                                value={filters.speed_index}
                                onChange={(value) => handleFilterChange('speed_index', value)}
                                allowClear
                                className="w-full"
                            >
                                {filterOptions?.speedIndexes?.map((index: string) => (
                                    <Select.Option key={index} value={index}>
                                        {index}
                                    </Select.Option>
                                ))}
                            </Select>
                        </Col>
                        <Col span={4}>
                            <Select
                                placeholder="📂 Kategori Seçin"
                                value={filters.category}
                                onChange={(value) => handleFilterChange('category', value)}
                                allowClear
                                className="w-full"
                            >
                                {filterOptions?.categories?.map((category: any) => (
                                    <Select.Option key={category.id} value={category.id}>
                                        {category.name} ({category.count})
                                    </Select.Option>
                                ))}
                            </Select>
                        </Col>
                        <Col span={8}>
                            <div className="text-sm text-gray-500">
                                {(() => {
                                    const activeFilters = Object.entries(filters).filter(([key, value]) =>
                                        key !== 'page' && key !== 'limit' && value && value !== ''
                                    ).length;
                                    return activeFilters > 0 ? `${activeFilters} aktif filtre` : 'Filtre yok';
                                })()}
                            </div>
                        </Col>
                    </Row>
                </Card>

                {/* Toplu Fiyat Güncelleme */}
                <Card title="Toplu Fiyat Güncelleme" className="mt-4">
                    <Row gutter={[16, 16]} align="middle">
                        <Col span={4}>
                            <InputNumber
                                placeholder="💰 Yüzde girin (örn: 10)"
                                value={priceUpdatePercentage}
                                onChange={(value) => setPriceUpdatePercentage(value || 0)}
                                min={0}
                                max={100}
                                className="w-full"
                                addonAfter="%"
                            />
                        </Col>
                        <Col span={4}>
                            <Button
                                type="primary"
                                onClick={() => handleBulkPriceUpdate('increase')}
                                loading={isPriceUpdateLoading}
                                className="w-full bg-green-600 hover:bg-green-700"
                            >
                                Fiyat Arttır
                            </Button>
                        </Col>
                        <Col span={4}>
                            <Button
                                type="primary"
                                danger
                                onClick={() => handleBulkPriceUpdate('decrease')}
                                loading={isPriceUpdateLoading}
                                className="w-full"
                            >
                                Fiyat İndir
                            </Button>
                        </Col>
                        <Col span={12}>
                            <div className="text-sm text-gray-500">
                                <strong>Uyarı:</strong> Bu işlem mevcut filtrelere uygun tüm ürünlerin fiyatlarını güncelleyecektir.
                                {(() => {
                                    const activeFilters = Object.entries(filters).filter(([key, value]) =>
                                        key !== 'page' && key !== 'limit' && value && value !== ''
                                    ).map(([key]) => {
                                        switch (key) {
                                            case 'brand': return 'Marka';
                                            case 'category': return 'Kategori';
                                            case 'season': return 'Mevsim';
                                            case 'tire_type': return 'Lastik Tipi';
                                            case 'tire_size': return 'Ebat';
                                            case 'production_year': return 'Üretim Yılı';
                                            case 'load_index': return 'Yük İndeksi';
                                            case 'speed_index': return 'Hız İndeksi';
                                            default: return key;
                                        }
                                    });
                                    return activeFilters.length > 0
                                        ? ` Aktif filtreler: ${activeFilters.join(', ')}`
                                        : ' Hiçbir filtre seçilmemiş - TÜM ürünler etkilenecek!';
                                })()}
                            </div>
                        </Col>
                    </Row>
                </Card>

                <Table
                    columns={columns as any}
                    dataSource={products}
                    rowKey="_id"
                    pagination={{
                        total: totalCount || products?.length || 0,
                        current: currentPage || 1,
                        pageSize: filters.limit,
                        showSizeChanger: true,
                        showQuickJumper: true,
                        showTotal: (total, range) => `${range[0]}-${range[1]} / ${total} kayıt`,
                        onChange: (page, pageSize) => {
                            const newFilters = { ...filters, page, limit: pageSize || 10 };
                            setFilters(newFilters);

                            // API için filtre parametrelerini hazırla
                            const apiParams: any = { page, limit: pageSize || 10 };
                            if (newFilters.search) apiParams.search = newFilters.search;
                            if (newFilters.brand) apiParams.brand = [newFilters.brand];
                            if (newFilters.tire_type) apiParams.tire_type = [newFilters.tire_type];
                            if (newFilters.season) apiParams.season = [newFilters.season];
                            if (newFilters.tire_size) apiParams.tire_size = newFilters.tire_size;
                            if (newFilters.production_year) apiParams.production_year = newFilters.production_year;
                            if (newFilters.category) apiParams.category = [newFilters.category];
                            if (newFilters.load_index) apiParams.load_index = [newFilters.load_index];
                            if (newFilters.speed_index) apiParams.speed_index = [newFilters.speed_index];

                            dispatch<any>(getProducts(apiParams));
                        }
                    }}
                />

                <Modal
                    title={editingProduct ? "Ürün Düzenle" : "Yeni Ürün Ekle"}
                    open={isModalVisible}
                    onCancel={handleCancel}
                    footer={null}
                    width={800}
                >
                    <Form
                        form={form}
                        layout="vertical"
                        onFinish={handleSubmit}
                    >
                        <div className="grid grid-cols-2 gap-4">
                            <Form.Item
                                name="name"
                                label="Ürün Adı"
                                rules={[{ required: true, message: 'Lütfen ürün adını girin' }]}
                            >
                                <Input />
                            </Form.Item>

                            <Form.Item
                                name="brand_name"
                                label="Marka"
                                rules={[{ required: true, message: 'Lütfen marka adını girin' }]}
                            >
                                <Input />
                            </Form.Item>

                            <Form.Item
                                name="tire_type"
                                label="Lastik Tipi"
                                rules={[{ required: true, message: 'Lütfen lastik tipini seçin' }]}
                            >
                                <Select>
                                    <Select.Option value="Kış Lastiği">Kış Lastiği</Select.Option>
                                    <Select.Option value="Yaz Lastiği">Yaz Lastiği</Select.Option>
                                    <Select.Option value="4 Mevsim Lastiği">4 Mevsim Lastiği</Select.Option>
                                </Select>
                            </Form.Item>

                            <Form.Item
                                name="price"
                                label="Normal Fiyat"
                                rules={[{ required: true, message: 'Lütfen normal fiyatı girin' }]}
                            >
                                <InputNumber min={0} style={{ width: '100%' }} />
                            </Form.Item>

                            <Form.Item
                                name="discounted_price"
                                label="İndirimli Fiyat"
                            >
                                <InputNumber min={0} style={{ width: '100%' }} />
                            </Form.Item>

                            <Form.Item
                                name="tire_size"
                                label="Lastik Ölçüsü (örn: 205/55R16)"
                                rules={[{ required: true, message: 'Lütfen lastik ölçüsünü girin' }]}
                            >
                                <Input placeholder="205/55R16" />
                            </Form.Item>

                            <Form.Item
                                name={['properties', 'season']}
                                label="Sezon"
                                rules={[{ required: true, message: 'Lütfen sezonu seçin' }]}
                            >
                                <Select>
                                    <Select.Option value="Kış">Kış</Select.Option>
                                    <Select.Option value="Yaz">Yaz</Select.Option>
                                    <Select.Option value="4 Mevsim">4 Mevsim</Select.Option>
                                </Select>
                            </Form.Item>

                            <Form.Item
                                name={['properties', 'width']}
                                label="Taban Genişliği"
                                rules={[{ required: true, message: 'Lütfen genişliği girin' }]}
                            >
                                <Input placeholder="205" />
                            </Form.Item>

                            <Form.Item
                                name={['properties', 'aspect_ratio']}
                                label="Yanak Yüksekliği"
                                rules={[{ required: true, message: 'Lütfen yanak yüksekliğini girin' }]}
                            >
                                <Input placeholder="55" />
                            </Form.Item>

                            <Form.Item
                                name={['properties', 'rim_diameter']}
                                label="Jant Çapı"
                                rules={[{ required: true, message: 'Lütfen jant çapını girin' }]}
                            >
                                <Input placeholder="16" />
                            </Form.Item>

                            <Form.Item
                                name={['properties', 'load_index']}
                                label="Yük İndeksi"
                            >
                                <Input placeholder="91" />
                            </Form.Item>

                            <Form.Item
                                name={['properties', 'speed_index']}
                                label="Hız İndeksi"
                            >
                                <Input placeholder="H" />
                            </Form.Item>

                            <Form.Item
                                name="production_year"
                                label="Üretim Yılı"
                            >
                                <Input placeholder="2024" />
                            </Form.Item>

                            <Form.Item
                                name="stock_amount"
                                label="Stok Miktarı"
                            >
                                <InputNumber min={0} style={{ width: '100%' }} />
                            </Form.Item>
                        </div>

                        <Form.Item
                            name="original_name"
                            label="Orijinal Ürün Adı"
                        >
                            <Input />
                        </Form.Item>

                        <Form.Item
                            label="Ürün Resimleri"
                            name="images"
                            rules={[{ required: true, message: 'Lütfen en az bir resim yükleyin' }]}
                        >
                            <Upload
                                listType="picture-card"
                                fileList={fileList}
                                onChange={({ fileList }) => setFileList(fileList)}
                                beforeUpload={() => false}
                                multiple
                                maxCount={5}
                            >
                                {fileList.length >= 5 ? null : (
                                    <div>
                                        <UploadOutlined />
                                        <div style={{ marginTop: 8 }}>Yükle</div>
                                    </div>
                                )}
                            </Upload>
                        </Form.Item>

                        <div className="flex justify-end space-x-2">
                            <Button onClick={handleCancel}>
                                İptal
                            </Button>
                            <Button type="primary" htmlType="submit">
                                {editingProduct ? 'Güncelle' : 'Oluştur'}
                            </Button>
                        </div>
                    </Form>
                </Modal>
            </div>
        </ProtectedLayout>
    );
};

export default Products; 